import { createFileRoute, Link } from "@tanstack/react-router";
import { LegalLayout } from "@/components/legal-layout";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" lastUpdated="December 24, 2025">
      <section className="space-y-6">
        <div>
          <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We take the protection of your personal data very seriously. This
            privacy policy explains what data we collect, how we use it, and
            your rights under the General Data Protection Regulation
            (GDPR/DSGVO) and the German Federal Data Protection Act (BDSG).
          </p>
          <p className="text-muted-foreground leading-relaxed">
            This privacy policy applies to all services offered by Harshil
            Agrawal (operating as Images AI) through our website and
            applications.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            2. Controller Information
          </h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-2">
              <strong>Data Controller:</strong>
            </p>
            <p>Harshil Agrawal</p>
            <p className="text-sm text-muted-foreground">
              Operating as: Images AI
            </p>
            <p className="mt-3">Helene-Jacobs-Str.</p>
            <p>14199 Berlin, Germany</p>
            <p className="mt-4">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:hey@harshil.dev"
                className="text-foreground hover:underline"
              >
                hey@harshil.dev
              </a>
            </p>
            <p className="mt-6 text-sm text-muted-foreground">
              For complete legal information, please see our{" "}
              <Link to="/impressum" className="text-foreground hover:underline">
                Impressum
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">3. Data We Collect</h2>

          <h3 className="text-xl font-semibold mb-3 mt-6">3.1 Account Data</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            When you create an account, we collect:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Email address</li>
            <li>Password (encrypted and hashed)</li>
            <li>Account creation date</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            <strong>Legal Basis:</strong> Article 6(1)(b) GDPR - Performance of
            contract
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">3.2 Usage Data</h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            When you use our AI image generation services, we collect:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Text prompts submitted for image generation</li>
            <li>Image generation settings and parameters</li>
            <li>Generated images (stored in encrypted cloud storage)</li>
            <li>Edit history and modifications</li>
            <li>Credit usage and subscription tier</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            <strong>Legal Basis:</strong> Article 6(1)(b) GDPR - Performance of
            contract
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.3 Payment Information
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            When you subscribe to a paid plan, we collect payment information
            through our payment processor Stripe:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Billing name and address</li>
            <li>
              Payment method details (processed by Stripe, not stored by us)
            </li>
            <li>Transaction history</li>
            <li>Subscription status</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            <strong>Legal Basis:</strong> Article 6(1)(b) GDPR - Performance of
            contract
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            3.4 Session and Authentication Data
          </h3>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We use cookies for authentication and session management:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Session cookies (essential for login functionality)</li>
            <li>Login timestamps</li>
            <li>Device information (browser type, operating system)</li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            <strong>Legal Basis:</strong> Article 6(1)(b) GDPR - Performance of
            contract
          </p>

          <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6 mt-6">
            <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
              <span className="text-yellow-600 dark:text-yellow-500">⚠️</span>
              3.5 Biometric Data Warning
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              <strong>Important:</strong> If you upload images containing faces
              for editing, these images may be classified as{" "}
              <strong>biometric data</strong> under Article 9 GDPR, which
              requires special protection.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-4">
              By uploading images with faces, you explicitly consent to the
              processing of this biometric data for the purpose of AI-powered
              image editing. We process this data through our third-party AI
              provider Replicate (see section 4.1).
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong>Legal Basis:</strong> Article 9(2)(a) GDPR - Explicit
              consent for biometric data processing
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            4. Third-Party Processors
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            We work with trusted third-party service providers to operate our
            service. All processors are bound by Data Processing Agreements
            (DPAs) and, where applicable, Standard Contractual Clauses (SCCs)
            for international data transfers.
          </p>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            4.1 Replicate AI (USA)
          </h3>
          <div className="bg-muted/50 border border-border rounded-2xl p-6 mb-6">
            <p className="mb-2">
              <strong>Purpose:</strong> AI image generation and editing
            </p>
            <p className="mb-2">
              <strong>Data Processed:</strong> Text prompts, uploaded images,
              generation parameters
            </p>
            <p className="mb-2">
              <strong>Location:</strong> United States
            </p>
            <p className="mb-4">
              <strong>International Transfer:</strong> Standard Contractual
              Clauses (SCCs) under Article 46 GDPR
            </p>
            <p className="text-sm text-muted-foreground">
              Data is transmitted to Replicate's servers in the USA for
              processing. We route all requests through Cloudflare AI Gateway
              for enhanced security and caching.
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            4.2 Cloudflare (Multi-Region)
          </h3>
          <div className="bg-muted/50 border border-border rounded-2xl p-6 mb-6">
            <p className="mb-2">
              <strong>Purpose:</strong> Infrastructure, hosting, content
              delivery, storage
            </p>
            <p className="mb-2">
              <strong>Data Processed:</strong> All application data, generated
              images, user sessions
            </p>
            <p className="mb-2">
              <strong>Services Used:</strong> Cloudflare Workers (compute), D1
              Database (database), R2 Storage (file storage), AI Gateway (API
              routing)
            </p>
            <p className="mb-2">
              <strong>Location:</strong> EU and worldwide (data residency
              configurable)
            </p>
            <p className="text-sm text-muted-foreground">
              Cloudflare is GDPR-compliant and certified under ISO 27001, SOC 2
              Type II.
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 mt-6">
            4.3 Stripe (USA/EU)
          </h3>
          <div className="bg-muted/50 border border-border rounded-2xl p-6 mb-6">
            <p className="mb-2">
              <strong>Purpose:</strong> Payment processing, subscription
              management
            </p>
            <p className="mb-2">
              <strong>Data Processed:</strong> Billing information, payment
              methods, transaction history
            </p>
            <p className="mb-2">
              <strong>Location:</strong> European Union and United States
            </p>
            <p className="mb-4">
              <strong>International Transfer:</strong> Standard Contractual
              Clauses (SCCs) where applicable
            </p>
            <p className="text-sm text-muted-foreground">
              Stripe is PCI DSS Level 1 certified and GDPR-compliant. Payment
              card details are processed directly by Stripe and never stored on
              our servers.
            </p>
          </div>

          <h3 className="text-xl font-semibold mb-3 mt-6">4.4 Better Auth</h3>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-2">
              <strong>Purpose:</strong> Authentication and session management
            </p>
            <p className="mb-2">
              <strong>Data Processed:</strong> Email addresses, password hashes,
              session tokens
            </p>
            <p className="mb-2">
              <strong>Location:</strong> Data stored in our Cloudflare D1
              database (EU)
            </p>
            <p className="text-sm text-muted-foreground">
              Better Auth is an open-source authentication library. All
              authentication data remains in our control within the EU.
            </p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            5. Purpose of Data Processing
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We process your personal data for the following purposes:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>To provide and maintain our AI image generation service</li>
            <li>To manage your account and authentication</li>
            <li>To process payments and manage subscriptions</li>
            <li>To store and display your generated images</li>
            <li>To provide customer support</li>
            <li>To comply with legal obligations</li>
            <li>To improve our services and develop new features</li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">6. Data Retention</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We retain your personal data for as long as necessary to provide our
            services and comply with legal obligations:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              <strong>Account data:</strong> Retained while your account is
              active and for 30 days after account deletion
            </li>
            <li>
              <strong>Generated images:</strong> Retained while your account is
              active; deleted within 30 days of account deletion
            </li>
            <li>
              <strong>Payment records:</strong> Retained for 10 years to comply
              with German tax law (§ 147 AO)
            </li>
            <li>
              <strong>Session data:</strong> Deleted after session expiry
              (typically 30 days)
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            7. International Data Transfers
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            Some of our service providers are located outside the European
            Economic Area (EEA). We ensure adequate protection through:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              <strong>Standard Contractual Clauses (SCCs):</strong> EU-approved
              contracts ensuring GDPR-level protection (Article 46 GDPR)
            </li>
            <li>
              <strong>Adequacy Decisions:</strong> Transfers to countries with
              adequate protection recognized by the EU Commission
            </li>
          </ul>
          <p className="text-muted-foreground leading-relaxed mt-4">
            For transfers to the USA (Replicate, Stripe), we use Standard
            Contractual Clauses and additional safeguards to ensure your data is
            protected.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            8. Your Rights Under GDPR
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Under the GDPR, you have the following rights regarding your
            personal data:
          </p>

          <div className="space-y-4">
            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Access (Article 15 GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                You can request a copy of all personal data we hold about you.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Rectification (Article 16 GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                You can request correction of inaccurate or incomplete personal
                data.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Erasure / "Right to be Forgotten" (Article 17 GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                You can request deletion of your personal data when it's no
                longer needed or if you withdraw consent. Note: Some data may be
                retained for legal compliance.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Data Portability (Article 20 GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                You can request your data in a structured, machine-readable
                format for transfer to another service.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Object (Article 21 GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                You can object to processing based on legitimate interests or
                for direct marketing purposes.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Restriction of Processing (Article 18 GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                You can request limitation of processing in certain
                circumstances.
              </p>
            </div>

            <div className="bg-muted/50 border border-border rounded-2xl p-6">
              <h3 className="font-semibold mb-2">
                Right to Withdraw Consent (Article 7(3) GDPR)
              </h3>
              <p className="text-muted-foreground text-sm">
                Where processing is based on consent, you can withdraw it at any
                time.
              </p>
            </div>
          </div>

          <p className="text-muted-foreground leading-relaxed mt-6">
            To exercise any of these rights, please contact us at{" "}
            <a
              href="mailto:hey@harshil.dev"
              className="text-foreground hover:underline"
            >
              hey@harshil.dev
            </a>
            . We will respond within 30 days.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            9. Cookies and Tracking
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We use cookies for essential functionality and to improve your
            experience. For detailed information about the cookies we use,
            please see our{" "}
            <Link to="/cookies" className="text-foreground hover:underline">
              Cookie Policy
            </Link>
            .
          </p>
          <p className="text-muted-foreground leading-relaxed">
            <strong>Essential cookies:</strong> Required for authentication and
            session management (no consent required under GDPR).
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">10. Security Measures</h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            We implement appropriate technical and organizational measures to
            protect your personal data:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>Encryption in transit (HTTPS/TLS) and at rest</li>
            <li>Password hashing using industry-standard algorithms</li>
            <li>Regular security audits and updates</li>
            <li>Access controls and authentication</li>
            <li>
              Secure cloud infrastructure (Cloudflare, ISO 27001 certified)
            </li>
            <li>Data backup and disaster recovery procedures</li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            11. Children's Privacy
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Our service is not intended for children under 16 years of age. We
            do not knowingly collect personal data from children under 16. If
            you believe we have collected data from a child under 16, please
            contact us immediately.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            12. AI Transparency (EU AI Act)
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            In accordance with the EU AI Act transparency obligations:
          </p>
          <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
            <li>
              All images on our platform are generated or edited using
              artificial intelligence
            </li>
            <li>
              We use state-of-the-art AI models provided by third-party AI
              services (Replicate)
            </li>
            <li>
              AI-generated content is clearly labeled throughout the application
            </li>
            <li>
              You retain full ownership of images you generate with our AI
              systems
            </li>
          </ul>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            13. Changes to This Privacy Policy
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            We may update this privacy policy from time to time to reflect
            changes in our practices or legal requirements. We will notify you
            of significant changes by email or through a notice on our website.
            The "Last Updated" date at the top indicates when this policy was
            last revised.
          </p>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            14. Contact Information
          </h2>
          <div className="bg-muted/50 border border-border rounded-2xl p-6">
            <p className="mb-4">
              For any questions or concerns about this privacy policy or our
              data practices, please contact us:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a
                href="mailto:hey@harshil.dev"
                className="text-foreground hover:underline"
              >
                hey@harshil.dev
              </a>
            </p>
            <p className="mt-4">
              <strong>Postal Address:</strong>
            </p>
            <p>Harshil Agrawal</p>
            <p>Helene-Jacobs-Str.</p>
            <p>14199 Berlin, Germany</p>
          </div>
        </div>
      </section>

      <section className="space-y-6 mt-12">
        <div>
          <h2 className="text-2xl font-semibold mb-4">
            15. Supervisory Authority
          </h2>
          <p className="text-muted-foreground leading-relaxed mb-4">
            You have the right to lodge a complaint with a supervisory
            authority, particularly in the EU member state of your habitual
            residence, place of work, or place of alleged infringement.
          </p>
          <p className="text-muted-foreground leading-relaxed">
            For Germany, the federal data protection authority is:
          </p>
          <div className="bg-muted/50 border border-border rounded-2xl p-6 mt-4">
            <p className="font-semibold mb-2">
              Der Bundesbeauftragte für den Datenschutz und die
              Informationsfreiheit (BfDI)
            </p>
            <p>Graurheindorfer Str. 153</p>
            <p>53117 Bonn, Germany</p>
            <p className="mt-2">
              Website:{" "}
              <a
                href="https://www.bfdi.bund.de"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:underline"
              >
                www.bfdi.bund.de
              </a>
            </p>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
}
